import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { db } from './firebase';

// Custom tick component for wrapping text
const CustomTick = ({ x, y, payload, dataLength, viewBox }) => {
    // Calculate approximate width per bar
    // Use viewBox width if available, otherwise estimate based on typical chart width
    const chartWidth = viewBox?.width || 800;
    const barCount = dataLength || 10;
    // Account for margins and calculate available width per bar
    // Use 70% of bar spacing to leave some padding
    const maxWidth = Math.max((chartWidth - 100) / barCount * 0.7, 50);
    
    const words = payload.value.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + ' ' + words[i];
        // Estimate text width (roughly 6.5px per character for 11px font)
        const testWidth = testLine.length * 6.5;
        if (testWidth > maxWidth && currentLine.length > 0) {
            lines.push(currentLine);
            currentLine = words[i];
        } else {
            currentLine = testLine;
        }
    }
    lines.push(currentLine);

    return (
        <g transform={`translate(${x},${y + 20})`}>
            <text x={0} y={0} dy={16} textAnchor="middle" fill="#666" fontSize={11}>
                {lines.map((line, index) => (
                    <tspan x={0} dy={index === 0 ? 0 : 14} key={index}>
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

export default function Stats() {
    const [topAttendees, setTopAttendees] = useState([]);
    const [popularShops, setPopularShops] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            if (!db) {
                setError("Firebase not initialized");
                setIsLoading(false);
                return;
            }

            try {
                // Get all daily_locations documents
                const dailyLocationsRef = collection(db, 'daily_locations');
                const snapshot = await getDocs(dailyLocationsRef);
                
                // Process attendance data
                const attendeeCounts = {};
                const shopCounts = {};
                
                snapshot.forEach((docSnap) => {
                    const data = docSnap.data();
                    
                    // Count attendees for each person
                    if (data.attendees && Array.isArray(data.attendees)) {
                        data.attendees.forEach((attendee) => {
                            const key = attendee.uid || attendee.firstName;
                            if (!attendeeCounts[key]) {
                                attendeeCounts[key] = {
                                    name: attendee.firstName || 'Unknown',
                                    count: 0,
                                    uid: attendee.uid
                                };
                            }
                            attendeeCounts[key].count++;
                        });
                    }
                    
                    // Count happy hour attendees
                    if (data.happyHourAttendees && Array.isArray(data.happyHourAttendees)) {
                        data.happyHourAttendees.forEach((attendee) => {
                            const key = attendee.uid || attendee.firstName;
                            if (!attendeeCounts[key]) {
                                attendeeCounts[key] = {
                                    name: attendee.firstName || 'Unknown',
                                    count: 0,
                                    uid: attendee.uid
                                };
                            }
                            attendeeCounts[key].count++;
                        });
                    }
                    
                    // Count coffee shop visits
                    if (data.morningLocation && data.morningLocation.name) {
                        const shopName = data.morningLocation.name;
                        shopCounts[shopName] = (shopCounts[shopName] || 0) + 
                            (data.attendees?.filter(a => a.session === 'morning').length || 0);
                    }
                    
                    if (data.afternoonLocation && data.afternoonLocation.name) {
                        const shopName = data.afternoonLocation.name;
                        shopCounts[shopName] = (shopCounts[shopName] || 0) + 
                            (data.attendees?.filter(a => a.session === 'afternoon').length || 0);
                    }
                });
                
                // Convert to arrays and sort, filtering out Brett
                const attendeesArray = Object.values(attendeeCounts)
                    .filter(attendee => {
                        // Filter out Brett (case-insensitive check)
                        const name = attendee.name?.toLowerCase() || '';
                        return name !== 'brett';
                    })
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 10)
                    .map((item, index) => ({
                        ...item,
                        rank: index + 1
                    }));
                
                const shopsArray = Object.entries(shopCounts)
                    .map(([name, count]) => ({ name, count }))
                    .sort((a, b) => b.count - a.count)
                    .slice(0, 10);
                
                setTopAttendees(attendeesArray);
                setPopularShops(shopsArray);
                setIsLoading(false);
            } catch (err) {
                console.error("Error fetching stats:", err);
                setError("Failed to load statistics. Please try again later.");
                setIsLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (isLoading) {
        return (
            <div className="bg-[#C02C00] min-h-screen font-montserrat antialiased text-gray-800">
                <div className="container mx-auto p-4">
                    <div className="text-center py-20">
                        <p className="text-lg text-white">Loading statistics...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-[#C02C00] min-h-screen font-montserrat antialiased text-gray-800">
                <div className="container mx-auto p-4">
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-md" role="alert">
                        <p className="font-bold">Error</p>
                        <p>{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#C02C00] min-h-screen font-montserrat antialiased text-gray-800">
            <div className="container mx-auto p-4">
                {/* Header */}
                <div className="flex items-center justify-between py-3 px-4 bg-black/30 backdrop-blur-sm rounded-lg shadow-sm mb-4">
                    <div className="text-sm font-bold text-white font-montserrat">
                        Coffee Shop Statistics
                    </div>
                    <Link 
                        to="/" 
                        className="bg-gradient-to-r from-white to-gray-200 text-black font-semibold py-1 px-3 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300 text-sm"
                    >
                        Back to Home
                    </Link>
                </div>

                <header className="flex items-center justify-center py-6">
                    <img 
                        src="/images/find-my-brett-logo.gif" 
                        alt="Find My Brett" 
                        className="w-full max-w-sm md:max-w-md lg:max-w-xl h-auto object-contain"
                    />
                </header>

                {/* Top Attendees Chart */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-6 mb-6">
                    <h2 className="text-3xl font-semibold mb-6 text-gray-800 font-clarendon text-center">
                        Top 10 Coffee Shop Attendees
                    </h2>
                    {topAttendees.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={topAttendees}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="name" 
                                        interval={0}
                                        tick={(props) => <CustomTick {...props} dataLength={topAttendees.length} />}
                                        height={50}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#3DA5D9" />
                                </BarChart>
                            </ResponsiveContainer>
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                {topAttendees.map((attendee) => (
                                    <div 
                                        key={attendee.uid || attendee.name} 
                                        className="bg-gradient-to-r from-[#3DA5D9]/20 to-[#3DA5D9]/40 p-4 rounded-lg text-center"
                                    >
                                        <div className="text-2xl font-bold text-gray-800">{attendee.count}</div>
                                        <div className="text-sm text-gray-600">{attendee.name}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-center text-gray-600 py-10">No attendance data available yet.</p>
                    )}
                </div>

                {/* Popular Coffee Shops Chart */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-6 mb-6">
                    <h2 className="text-3xl font-semibold mb-6 text-gray-800 font-clarendon text-center">
                        Most Popular Coffee Shops
                    </h2>
                    {popularShops.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={popularShops}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
                                        dataKey="name" 
                                        interval={0}
                                        tick={(props) => <CustomTick {...props} dataLength={popularShops.length} />}
                                        height={60}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#fec601" />
                                </BarChart>
                            </ResponsiveContainer>
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                {popularShops.map((shop) => (
                                    <div 
                                        key={shop.name} 
                                        className="bg-gradient-to-r from-[#fec601]/20 to-[#fec601]/40 p-4 rounded-lg text-center"
                                    >
                                        <div className="text-2xl font-bold text-gray-800">{shop.count}</div>
                                        <div className="text-sm text-gray-600">{shop.name}</div>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className="text-center text-gray-600 py-10">No coffee shop data available yet.</p>
                    )}
                </div>

                <footer className="text-center text-white text-xs mt-8 pb-4">
                    <img src="/images/schaefer-building.svg" alt="Schaefer Advertising" className="w-20 h-20 mx-auto p-4" />
                    <p>Made with coffee for the Schaefer team.</p>
                </footer>
            </div>
        </div>
    );
}

